import { Button } from "@/components/ui/button";
import { ArrowRight, Wifi, ChevronDown, Cloud, Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ravenLogo from "@/assets/raven-logo.png";

export function LandingHero() {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 landing-hero-gradient">
        {/* Floating Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-raven-electric/20 blur-3xl animate-float-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-raven-violet/20 blur-3xl animate-float-slower" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Column */}
          <div className="space-y-8">
            {/* Logo with Glow */}
            <div 
              className={`transform transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <div className="relative inline-block">
                <div className="absolute inset-0 blur-2xl bg-gradient-to-r from-raven-electric to-raven-violet opacity-50 animate-pulse" />
                <img 
                  src={ravenLogo} 
                  alt="Raven Logo" 
                  className="relative w-16 h-16 lg:w-20 lg:h-20 drop-shadow-2xl" 
                />
              </div>
            </div>

            {/* Co-created Badge */}
            <div 
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-xl bg-gradient-to-r from-raven-electric/20 to-raven-violet/20 border border-raven-electric/50 transform transition-all duration-700 delay-50 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <span className="text-sm font-semibold text-raven-electric">
                🤝 Co-creado con profesionales del sector
              </span>
            </div>

            {/* Title with Gradient */}
            <h1 
              className={`text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight transform transition-all duration-700 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <span className="landing-gradient-text">
                Gestión Digital para
              </span>
              <br />
              <span className="text-foreground">Seguridad y Contraincendios</span>
            </h1>

            {/* Subtitle */}
            <p 
              className={`text-lg sm:text-xl text-muted-foreground max-w-xl transform transition-all duration-700 delay-200 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              Desarrollado junto a empresarios del sector. <strong className="text-foreground">Comienza a trabajar al instante</strong> - sin instalaciones, sin esperas. Un sistema completo que trabaja offline, sincroniza automáticamente y digitaliza todos tus procesos.
            </p>

            {/* Features Pills */}
            <div 
              className={`flex flex-wrap gap-3 transform transition-all duration-700 delay-300 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              {[
                { icon: Wifi, text: 'Funciona Offline' },
                { icon: Cloud, text: 'Sync Automático' },
                { icon: Smartphone, text: 'PWA Nativa' }
              ].map((feature, i) => (
                <div 
                  key={i}
                  className="group px-4 py-2 rounded-full backdrop-blur-xl bg-gradient-to-r from-raven-electric/10 to-raven-violet/10 border border-white/10 hover:border-raven-electric/50 transition-all duration-300 hover:scale-105 cursor-default"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <feature.icon className="w-4 h-4 text-raven-electric group-hover:text-raven-violet transition-colors" />
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div 
              className={`flex flex-wrap gap-4 transform transition-all duration-700 delay-400 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <Button 
                variant="premium"
                size="lg"
                onClick={scrollToContact}
                className="group px-8 py-6 text-lg"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Solicitar Demo Gratuita
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:animate-shimmer" />
              </Button>

              <Button 
                variant="glass"
                size="lg"
                onClick={() => navigate('/login')}
                className="px-8 py-6 text-lg"
              >
                Iniciar Sesión
              </Button>
            </div>
          </div>

          {/* Right Column - Dashboard Mockup */}
          <div 
            className={`relative transform transition-all duration-1000 delay-500 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
            }`}
          >
            <div className="relative group">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-raven-electric/30 to-raven-violet/30 blur-3xl animate-pulse" />
              
              {/* Dashboard Mockup */}
              <div className="relative backdrop-blur-xl bg-gradient-to-br from-card/80 to-card/40 rounded-2xl border border-white/10 shadow-2xl overflow-hidden group-hover:scale-[1.02] transition-transform duration-500 p-6">
                {/* Browser Chrome */}
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/10">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  <div className="ml-4 text-xs text-muted-foreground font-medium">raven.app</div>
                </div>
                
                {/* Mock Dashboard Content */}
                <div className="space-y-4">
                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "248", label: "Clientes" },
                      { value: "156", label: "Órdenes" },
                      { value: "89%", label: "Completadas" }
                    ].map((stat, i) => (
                      <div 
                        key={i}
                        className="bg-gradient-to-br from-raven-electric/10 to-raven-violet/10 rounded-lg p-3 border border-white/10"
                      >
                        <div className="text-lg font-bold text-foreground">{stat.value}</div>
                        <div className="text-xs text-muted-foreground">{stat.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Chart Placeholder */}
                  <div className="bg-gradient-to-br from-card/60 to-card/30 rounded-lg p-4 border border-white/10">
                    <div className="text-xs text-muted-foreground mb-3">Actividad Reciente</div>
                    <div className="flex items-end justify-between gap-1 h-20">
                      {[30, 50, 40, 70, 45, 80, 60, 85].map((height, i) => (
                        <div 
                          key={i}
                          className="flex-1 bg-gradient-to-t from-raven-electric to-raven-violet rounded-t opacity-60 hover:opacity-100 transition-opacity"
                          style={{height: `${height}%`}}
                        />
                      ))}
                    </div>
                  </div>

                  {/* List Items */}
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div 
                        key={i}
                        className="flex items-center gap-3 bg-white/5 rounded-lg p-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center text-xs font-bold text-white">
                          {i}
                        </div>
                        <div className="flex-1">
                          <div className="h-2 bg-white/20 rounded w-3/4 mb-2" />
                          <div className="h-2 bg-white/10 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-2 rounded-full shadow-2xl animate-bounce">
                <span className="text-white font-bold text-sm flex items-center gap-2">
                  <Wifi className="w-4 h-4" />
                  Offline Ready
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden lg:block">
        <ChevronDown className="w-6 h-6 text-muted-foreground" />
      </div>
    </section>
  );
}
